import React from 'react';
import { Link } from 'react-router-dom';
import { Bot, CheckCircle, ArrowRight, MessageSquare, Zap, BarChart, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { mockServices } from '../mock';
import SEO from '../components/SEO';

const BotConsulting = () => {
  const service = mockServices.find(s => s.slug === 'bot-consulting');

  const useCases = [
    {
      icon: MessageSquare,
      title: 'Product Engineering',
      description: 'Build scalable products with dedicated engineering teams aligned with your culture and goals'
    },
    {
      icon: Zap,
      title: 'Technical Support',
      description: 'Establish 24/7 global support coverage with highly skilled technical support engineers'
    },
    {
      icon: BarChart,
      title: 'Sales & Marketing',
      description: 'Scale your revenue operations with dedicated sales development and marketing teams in India'
    },
    {
      icon: Clock,
      title: 'Finance & Operations',
      description: 'Optimize back-office operations with shared services and comprehensive support functions'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="GCC Consulting — Build, Operate, Transfer Model | NIVA Digital AI"
        description="Establish your Global Capability Centre in India with NIVA Digital. Build, operate, and transfer model for product engineering, support, sales, and operations teams."
        keywords="GCC consulting, global capability centre India, teams India, BOT model, build operate transfer, product engineering"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/bot-consulting`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-pink-50 via-red-50 to-orange-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1783628131231-8c55d2909526?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')" }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/75 via-white/55 to-white/75" aria-hidden="true" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Bot size={20} />
                <span>GCC Consulting Services</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Global Capability Centres
                <span className="block text-red-600 mt-2">in India</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Build, Operate, and Transfer high-performance Global Capability Centres in India. Scale rapidly with dedicated teams that deliver enterprise-grade solutions with startup-level agility.
              </p>
              <Link to="/gcc-cost-calculator">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Cost Calculator <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1501324682324-0d8d7c4b9706?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
                  alt="Modern corporate office building"
                  className="w-full h-full object-cover"
                  data-testid="gcc-hero-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our GCC Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive services to establish and scale your Global Capability Centre in India
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700 font-medium">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Functions We Scale
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover how our GCC services can help you scale across multiple business functions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => {
              const IconComponent = useCase.icon;
              return (
                <Card key={index} className="border-2 hover:shadow-xl transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-red-600" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{useCase.title}</h3>
                    <p className="text-gray-600">{useCase.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Build-Operate-Transfer Model
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
              Our proven methodology ensures seamless automation implementation and knowledge transfer
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-red-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Build</h3>
              <p className="text-gray-600">Assess growth goals, define talent strategy, and establish your GCC foundation with culture-first hiring in India</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-red-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Operate</h3>
              <p className="text-gray-600">Manage day-to-day operations with robust governance, employee well-being focus, and operational readiness within weeks</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-red-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Transfer</h3>
              <p className="text-gray-600">Seamless handover with clear protocols, job mapping, and structured offboarding for self-sustaining operations</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-red-600" size={32} />
                </div>
                <p className="text-gray-700 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              GCC Consulting Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real-world scenarios where our GCC services drive business value
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                    <MessageSquare className="text-red-600" size={24} />
                  </div>
                  SaaS Startup Scaling
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Fast-growing SaaS company needed to scale engineering team rapidly without burning capital.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Established 20-person product engineering GCC in Bengaluru with full-stack developers, QA engineers, and DevOps specialists.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 60% cost savings, 3x faster team scaling, maintained product velocity
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                    <Zap className="text-red-600" size={24} />
                  </div>
                  Enterprise Support Center
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Global enterprise needed 24/7 technical support coverage across multiple time zones.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Built dedicated technical support GCC with L1, L2, and L3 support engineers providing round-the-clock coverage.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 24/7 coverage achieved, 40% reduction in support costs, improved customer satisfaction
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                    <BarChart className="text-red-600" size={24} />
                  </div>
                  Infrastructure Hub
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Technology company needed specialized infrastructure and cloud operations capabilities.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Created infrastructure-focused GCC with cloud architects, DevOps engineers, and SREs in India.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: Access to top-tier infrastructure talent, 50% cost savings, improved system reliability
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                    <Clock className="text-red-600" size={24} />
                  </div>
                  Back-Office Operations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Mid-market company needed to optimize finance, HR, and administrative operations.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Established shared services GCC handling accounting, payroll, HR operations, and procurement.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 55% operational cost reduction, standardized processes, scalable back-office
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Build Your GCC in India?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Schedule a free consultation to discuss your Global Capability Centre needs
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us Today <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BotConsulting;